// Lightweight markdown renderer for AI responses (headings, bold, lists, hr, tables)
import React from "react";

function inline(text) {
  const html = text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-secondary text-xs">$1</code>');
  return { __html: html };
}

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // blank
    if (!trimmed) { out.push(<div key={key++} className="h-2" />); i++; continue; }

    // horizontal rule
    if (/^([-*_])\1{2,}$/.test(trimmed)) {
      out.push(<hr key={key++} className="my-3 border-border" />);
      i++; continue;
    }

    // table: current line has pipes and next line is a separator (---|---)
    if (trimmed.includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(lines[i + 1])) {
      const header = trimmed.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
        i++;
      }
      out.push(
        <div key={key++} className="my-3 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>{header.map((h, hi) => <th key={hi} className="text-left font-semibold px-3 py-2" dangerouslySetInnerHTML={inline(h)} />)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-border/60">
                  {r.map((c, ci) => <td key={ci} className="px-3 py-2 tabular-nums" dangerouslySetInnerHTML={inline(c)} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // headings
    const hMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (hMatch) {
      out.push(<div key={key++} className="font-heading font-semibold text-base mt-3 mb-1 text-primary" dangerouslySetInnerHTML={inline(hMatch[2])} />);
      i++; continue;
    }

    // bullet
    if (/^[-*]\s+/.test(trimmed)) {
      out.push(
        <div key={key++} className="flex gap-2 my-0.5 text-sm">
          <span className="mt-2 h-1 w-1 rounded-full bg-primary shrink-0" />
          <span dangerouslySetInnerHTML={inline(trimmed.replace(/^[-*]\s+/, ""))} />
        </div>
      );
      i++; continue;
    }

    // ordered
    if (/^\d+\.\s+/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)\./)[1];
      out.push(
        <div key={key++} className="flex gap-2 my-0.5 text-sm">
          <span className="font-semibold text-primary shrink-0">{num}.</span>
          <span dangerouslySetInnerHTML={inline(trimmed.replace(/^\d+\.\s+/, ""))} />
        </div>
      );
      i++; continue;
    }

    // paragraph
    out.push(<div key={key++} className="text-sm my-0.5 leading-relaxed" dangerouslySetInnerHTML={inline(trimmed)} />);
    i++;
  }
  return out;
}
